import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { closeAllPositionsActionBunja } from '@flipster/features/src/shared/trade-actions/close-all-positions/state';
import { SuspendedPositionsNoticeModal } from '@flipster/features/src/shared/trading-suspension/suspended-positions-notice-modal';
import { useOverlay } from '@flipster/overlay';
import type { OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import { PendingPosition, ordersBunja } from '@flipster/r230522/original/order';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import type { OpenPosition as LegacyOpenPosition } from '@flipster/r230522/original/position';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { alert } from '@flipster/ui/Alert';
import { getFlipsterConfigsV1Key, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';

import { SymbolScope } from '@web/app/[locale]/(web)/trade/_misc/symbol';
import LossProtectedSection from '@web/components/LossProtected/LossProtectedSection';

/** total open position / pending position count */
export const positionCountBunja = bunja(() => {
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { pendingPositionsAtom } = bunja.use(ordersBunja);

  return atom((get) => {
    const openPositions = get(openPositionsAtom);
    const pendingPositions = get(pendingPositionsAtom);
    const [openCount, pendingCount] = [openPositions, pendingPositions].map((positions) =>
      positions
        ? Object.values<LegacyOpenPosition[] | PendingPosition[]>(positions).reduce(
            (totalCount, positionBySymbol) => totalCount + positionBySymbol.length,
            0,
          )
        : 0,
    );
    return { openCount, pendingCount };
  });
});
positionCountBunja.debugLabel = 'positionCountBunja';

/** current symbol open position / pending position count */
export const symbolPositionCountBunja = bunja(() => {
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { pendingPositionsAtom } = bunja.use(ordersBunja);
  const tradeSymbol = bunja.use(SymbolScope);

  return atom((get) => {
    const openPositions = get(openPositionsAtom);
    const pendingPositions = get(pendingPositionsAtom);

    if (!tradeSymbol) return { symbolOpenCount: 0, symbolPendingCount: 0 };

    const [symbolOpenCount, symbolPendingCount] = [openPositions?.[tradeSymbol], pendingPositions?.[tradeSymbol]].map(
      (positions) => positions?.length ?? 0,
    );

    return { symbolOpenCount, symbolPendingCount };
  });
});
symbolPositionCountBunja.debugLabel = 'positionCountBunja';

interface ClosePositionsOptions {
  positions: (OpenPosition | LegacyOpenPosition)[];
  onAlertConfirmClick?: () => void;
  /** Sub-account ID for copy trading. If omitted, treated as main account. */
  accountId?: number;
}

/**
 * Close-all의 UI 오케스트레이션 훅.
 * 실행(분리/정렬/API/결과 분류)은 closeAllPositionsActionBunja(features/trade-actions)로 위임하고,
 * 여기선 confirm alert + skip-localStorage, 결과 alert 분기, suspended 노티스 모달, 성공 토스트를 담당한다.
 */
export function useClosePositions() {
  const transCommon = useTranslations('Common.r250401');
  const transAssets = useTranslations('Assets.r250401');
  const transPositionClose = useTranslations('Position_close.r250401');
  const transPositionFlip = useTranslations('Position_flip.r250401');

  const { user } = useAuth();
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const overlay = useOverlay();

  const { closeAllPositions, isSymbolCurrentlyTradableAtom, isCloseAllLoadingAtom } =
    useBunja(closeAllPositionsActionBunja);
  const isSymbolCurrentlyTradable = useAtomValue(isSymbolCurrentlyTradableAtom);
  const isLoading = useAtomValue(isCloseAllLoadingAtom);

  const storageKey = user && getFlipsterConfigsV1Key(user.userId, 'skipCloseAllPositionsConfirm');
  const canSkip = (storageKey && (getLocalStorage(storageKey) as boolean)) || false;

  const toSymbolGroup = (items: { symbol: string }[]) => {
    const countBySymbol = items.reduce<Record<string, number>>((acc, p) => {
      acc[p.symbol] = (acc[p.symbol] ?? 0) + 1;
      return acc;
    }, {});
    return Object.entries(countBySymbol).map(([symbol, count]) => ({
      symbol,
      displayShortName: instrumentsKeyBySymbol?.[symbol]?.displayShortName ?? symbol,
      count,
    }));
  };

  const openSuspendedNoticeModal = (
    suspendedPositions: (OpenPosition | LegacyOpenPosition)[],
    closedItems: { symbol: string }[] = [],
  ) => {
    overlay.open(({ isOpen, onClose, unmount }) => (
      <SuspendedPositionsNoticeModal
        isOpen={isOpen}
        onClose={onClose}
        onExit={unmount}
        closedSymbols={toSymbolGroup(closedItems)}
        suspendedSymbols={toSymbolGroup(suspendedPositions)}
      />
    ));
  };

  const closePositions = async ({ positions, accountId }: Omit<ClosePositionsOptions, 'onAlertConfirmClick'>) => {
    const outcome = await closeAllPositions({ positions, accountId });
    if (!outcome) return; // 에러는 액션에서 스낵바 처리됨

    switch (outcome.kind) {
      case 'allFailed':
        alert.show({
          title: transPositionClose('Close_all_failed_title'),
          message: transPositionClose('Generic_close_error_description'),
          confirmText: transCommon('Dismiss'),
        });
        return;
      case 'someFailed':
        alert.show({
          title: transPositionClose('Close_all_failed_with_count_title', {
            failedCount: outcome.failedCount,
            totalCount: outcome.totalCount,
          }),
          message: transPositionClose('Generic_close_error_description'),
          confirmText: transCommon('Dismiss'),
        });
        return;
      case 'allPartial':
        alert.show({
          title: transPositionClose('All_partially_closed_title'),
          message: transPositionClose('Partially_closed_market_condition_description'),
          confirmText: transCommon('Dismiss'),
        });
        return;
      case 'partialAndSuccess':
        alert.show({
          title: transPositionClose('Partially_closed_and_success_title', {
            partialCount: outcome.partialCount,
            successCount: outcome.successCount,
          }),
          message: transPositionClose('Partially_closed_and_success_description'),
          confirmText: transCommon('Dismiss'),
        });
        return;
      case 'partialAndFailed':
        alert.show({
          title: transPositionClose('Partially_closed_and_failed_title', {
            partialCount: outcome.partialCount,
            failedCount: outcome.failedCount,
          }),
          message: transPositionClose('Partially_closed_and_failed_description'),
          confirmText: transCommon('Dismiss'),
        });
        return;
      case 'partialFailedSuccess':
        alert.show({
          title: transPositionClose('Partially_closed_failed_success_title', {
            partialCount: outcome.partialCount,
            failedCount: outcome.failedCount,
            successCount: outcome.successCount,
          }),
          message: transPositionClose('Partially_closed_and_failed_description'),
          confirmText: transCommon('Dismiss'),
        });
        return;
      case 'suspendedRemainder':
        // No non-RWA failures: only RWA positions didn't close → show suspended notice modal
        openSuspendedNoticeModal(outcome.suspendedPositions, outcome.closedItems);
        return;
      case 'success':
        snackbar.success({ message: transPositionClose('Close_all_toast') });
        return;
      default:
        outcome satisfies never;
    }
  };

  const tryClosePositions = async (options: ClosePositionsOptions) => {
    const allSuspended =
      options.positions.length > 0 && options.positions.every((p) => !isSymbolCurrentlyTradable(p.symbol));
    if (allSuspended) {
      openSuspendedNoticeModal(options.positions);
      return;
    }
    if (canSkip) {
      return closePositions(options);
    }
    let isChecked = false;
    alert.show({
      title: transAssets('Close_all'),
      message: (
        <div className={flex({ flexDirection: 'column', gap: '16px' })}>
          {transPositionClose('Close_all_description')}
          <LossProtectedSection css={css.raw({ pb: 16, borderBottom: '1px solid {colors.border.default.level-2}' })} />
          <Checkbox
            onChange={(e) => {
              isChecked = e.target.checked;
            }}
          >
            {transPositionFlip('Dont_show_preview_again')}
          </Checkbox>
        </div>
      ),
      confirmText: transAssets('Close_all'),
      onConfirm: async () => {
        options.onAlertConfirmClick?.();
        await closePositions(options);
        if (isChecked && storageKey) setLocalStorage(storageKey, true);
      },
      closeText: transCommon('Cancel'),
      withClose: true,
    });
  };

  const hasSuspendedPositions = (positions: OpenPosition[]): boolean =>
    positions.some((p) => !isSymbolCurrentlyTradable(p.symbol));

  return {
    tryClosePositions,
    canSkip,
    isLoading,
    hasSuspendedPositions,
  };
}
